// src/config/compliance.js
//
// TRIPLE-MODE: EDU | PRE | POST
//   EDU  → pura educazione finanziaria, zero riferimenti OCF
//   PRE  → (storico) prima dell'iscrizione all'Albo — testi non più attuali, non usare
//   POST → titolare iscritto all'Albo (attuale)
//
// Per cambiare modalità: modifica REACT_APP_SITE_MODE nel file .env e rideploy.
// Fallback automatico a POST se la variabile non è definita.
//
// Revisione 29/09/2026: "iscritto all'Albo" e "servizio avviato" sono due fatti distinti.
// In POST il titolare è iscritto, ma il servizio di consulenza NON è avviato: i testi POST
// qui sotto descrivono questo stato (niente CTA di consulenza, niente "su richiesta").
// SERVICE_ACTIVE resta false finché non sono adempiuti gli obblighi per l'avvio (inclusa
// l'adesione all'ACF). Portarlo a true NON basta: servono testi e pagine riscritti per il
// servizio attivo (le versioni precedenti sono conservate nella copia d'archivio).

const RAW = process.env.REACT_APP_SITE_MODE;
const VALID_MODES = ["EDU", "PRE", "POST"];

export const MODE = VALID_MODES.includes(RAW) ? RAW : "POST";

// Il servizio di consulenza in materia di investimenti non è avviato.
export const SERVICE_ACTIVE = false;

// Retro-compatibilità: ISCRITTO_OCF è true solo in POST
export const ISCRITTO_OCF = MODE === "POST";

// Etichette marketing (IT/EN)
export const MARKETING_LABEL = "Comunicazione di marketing";
export const MARKETING_LABEL_EN = "Marketing communication";

/** ===========================
 *  TESTI ITALIANO
 *  =========================== */
export const TEXT_IT = {
  EDU: {
    banner:
      "Contenuti a scopo esclusivamente educativo e informativo. Non costituiscono consulenza finanziaria né sollecitazione all'investimento.",
    ctaPrimary: "Iscriviti alla newsletter",
    ctaSecondary: "Guarda il canale",
    services: [
      { title: "Analisi Quantitativa", desc: "Backtest, metriche e metodologie per investitori consapevoli." },
      { title: "Deep Learning & AI", desc: "Applicazioni di machine learning ai mercati finanziari." },
      { title: "Asset Allocation", desc: "Strategie di diversificazione e gestione del rischio." },
      { title: "Formazione Finanziaria", desc: "Workshop e contenuti per migliorare la cultura finanziaria." },
      { title: "Soluzioni Digitali", desc: "Tool e dashboard per analisi e simulazioni." },
      { title: "Knowledge Base", desc: "Articoli approfonditi su finanza quantitativa e mercati." }
    ],
    solutionsIntro:
      "Metodologie di ricerca e casi studio a fini educativi. Nessuna consulenza personalizzata.",
    pageDisclaimer:
      "Rendimenti passati non garantiscono risultati futuri. I contenuti sono esclusivamente educativi/informativi e non costituiscono consulenza finanziaria, offerta o sollecitazione all'investimento."
  },
  PRE: {
    banner:
      "Contenuti a fini informativi/educativi. Nessuna consulenza personalizzata o invito all'investimento prima dell'iscrizione OCF.",
    ctaPrimary: "Iscriviti alla newsletter",
    ctaSecondary: "Ricevi aggiornamenti",
    services: [
      { title: "Analisi Portafoglio (educational)", desc: "Approfondimenti e benchmark didattici, senza raccomandazioni personalizzate." },
      { title: "Strategie e Asset (ricerca)", desc: "Metodologie e razionali quantitativi presentati a scopo informativo." },
      { title: "Analisi Quantitativa", desc: "Backtest, metriche e paper digest per investitori consapevoli." },
      { title: "Formazione Finanziaria", desc: "Workshop e contenuti per migliorare la cultura finanziaria." },
      { title: "Soluzioni Digitali", desc: "Tool e dashboard dimostrativi, non costituenti consulenza." },
      { title: "Q&A Informativo", desc: "Sessioni di domande/risposte non personalizzate." }
    ],
    solutionsIntro:
      "Metodologie di ricerca e casi studio. Contenuti a fini informativi: nessuna consulenza prima dell'iscrizione OCF.",
    pageDisclaimer:
      "Rendimenti passati non garantiscono risultati futuri. I contenuti hanno finalità esclusivamente informative/educative e non costituiscono consulenza finanziaria, offerta o sollecitazione. La consulenza sarà prestata solo dopo iscrizione all'Albo OCF."
  },
  POST: {
    banner:
      "Contenuti informativi ed educativi. Il servizio di consulenza in materia di investimenti non è attualmente avviato: nessuna raccomandazione personalizzata.",
    ctaPrimary: "Iscriviti agli aggiornamenti",
    ctaSecondary: "Contatti",
    services: [
      { title: "Knowledge Base", desc: "Articoli su ETF, asset allocation, rischio e metodi quantitativi, a fini informativi ed educativi." },
      { title: "Ricerca quantitativa", desc: "Modelli, backtest e validazione descritti in chiave metodologica, con fonti e limiti dichiarati." },
      { title: "Serie video e testi", desc: "Serie su mercati, derivati, deep learning e metodo, pubblicate sul canale YouTube e sul sito." },
      { title: "Preparazione all'esame OCF", desc: "Materiali di studio sulle materie dell'esame di abilitazione." },
      { title: "Strumenti di simulazione", desc: "Simulatori e calcolatori con ipotesi esplicite: non producono raccomandazioni personalizzate." },
      { title: "Trasparenza", desc: "Stato del servizio, iscrizione all'Albo del titolare e limiti dei contenuti." }
    ],
    solutionsIntro:
      "Metodologie quantitative descritte in chiave metodologica, con fonti scientifiche e senza promesse di rendimento: come vengono costruiti i modelli e, soprattutto, come vengono validati prima di dar loro fiducia. Sono contenuti informativi ed educativi: non costituiscono raccomandazioni personalizzate né un prodotto o servizio sottoscrivibile.",
    pageDisclaimer:
      "I contenuti hanno finalità informative ed educative e non costituiscono consulenza in materia di investimenti, raccomandazione personalizzata, offerta o sollecitazione. I rendimenti passati o simulati non sono indicativi di quelli futuri e ogni investimento comporta rischi, inclusa la perdita del capitale."
  }
};

/** ===========================
 *  TESTI INGLESE
 *  =========================== */
export const TEXT_EN = {
  EDU: {
    banner:
      "Content for educational and informational purposes only. Does not constitute financial advice or investment solicitation.",
    ctaPrimary: "Subscribe to the newsletter",
    ctaSecondary: "Watch the channel",
    services: [
      { title: "Quantitative Analysis", desc: "Backtests, metrics and methodologies for informed investors." },
      { title: "Deep Learning & AI", desc: "Machine learning applications in financial markets." },
      { title: "Asset Allocation", desc: "Diversification strategies and risk management." },
      { title: "Financial Education", desc: "Workshops and content to improve financial literacy." },
      { title: "Digital Solutions", desc: "Tools and dashboards for analysis and simulations." },
      { title: "Knowledge Base", desc: "In-depth articles on quantitative finance and markets." }
    ],
    solutionsIntro:
      "Research methodologies and case studies for educational purposes. No personalized advisory services.",
    pageDisclaimer:
      "Past performance does not guarantee future results. Content is exclusively educational/informational and does not constitute financial advice, an offer or investment solicitation."
  },
  PRE: {
    banner:
      "Content for informational/educational purposes. No personalized advice or investment solicitation before OCF registration.",
    ctaPrimary: "Subscribe to the newsletter",
    ctaSecondary: "Get updates",
    services: [
      { title: "Portfolio Analysis (educational)", desc: "Insights and educational benchmarks, without personalized recommendations." },
      { title: "Strategies & Assets (research)", desc: "Quantitative methods and rationales presented for information only." },
      { title: "Quantitative Analysis", desc: "Backtests, metrics and paper digests for informed investors." },
      { title: "Financial Education", desc: "Workshops and content to improve financial literacy." },
      { title: "Digital Solutions", desc: "Demonstrative tools and dashboards; not investment advice." },
      { title: "Informational Q&A", desc: "Non-personalized Q&A sessions." }
    ],
    solutionsIntro:
      "Research methodologies and case studies. Informational content: no advisory services before OCF registration.",
    pageDisclaimer:
      "Past performance does not guarantee future results. Content is for informational/educational purposes only and does not constitute financial advice, an offer or solicitation. Advisory services will be provided only after registration with the OCF."
  },
  POST: {
    banner:
      "Informational and educational content. The investment advice service has not yet commenced: no personalised recommendations.",
    ctaPrimary: "Subscribe to updates",
    ctaSecondary: "Contact",
    services: [
      { title: "Knowledge Base", desc: "Articles on ETFs, asset allocation, risk and quantitative methods, for informational and educational purposes." },
      { title: "Quantitative research", desc: "Models, backtests and validation described methodologically, with sources and stated limits." },
      { title: "Video and text series", desc: "Series on markets, derivatives, deep learning and method, published on the YouTube channel and on the site." },
      { title: "OCF exam preparation", desc: "Study materials on the subjects of the Italian adviser qualification exam." },
      { title: "Simulation tools", desc: "Simulators and calculators with explicit assumptions: they produce no personalised recommendations." },
      { title: "Transparency", desc: "Service status, the owner's registration in the Italian register and the limits of the content." }
    ],
    solutionsIntro:
      "Quantitative methodologies described from a methodological standpoint, with scientific sources and no return promises: how the models are built and — above all — how they are validated before being trusted. This is informational and educational content: it is not a personalised recommendation, nor a product or service that can be subscribed to.",
    pageDisclaimer:
      "The content is for informational and educational purposes and does not constitute investment advice, a personalised recommendation, an offer or a solicitation. Past or simulated returns are not indicative of future ones, and every investment involves risks, including the loss of capital."
  }
};

// Retro-compatibilità: TEXT è l'italiano
export const TEXT = TEXT_IT;

// Helpers per i componenti
export const getTEXT = (lang) => (lang === "en" ? TEXT_EN : TEXT_IT);
export const getMarketingLabel = (lang) => (lang === "en" ? MARKETING_LABEL_EN : MARKETING_LABEL);

// Helper utile per i componenti che devono sapere se mostrare CTA consulenza
export const showConsultancyCTA = MODE === "POST" && SERVICE_ACTIVE;

// Avviso obbligatorio sullo stato del servizio (testo esatto, IT/EN). Mostrato finché
// SERVICE_ACTIVE è false: nel footer di tutte le pagine, come sezione della pagina
// Trasparenza (#stato-servizio) e nel §3 dei Termini.
export const SERVICE_STATUS_NOTICE = {
  it: {
    title: "Stato del servizio",
    paras: [
      "La prestazione del servizio di consulenza in materia di investimenti non è attualmente avviata. Il sito mette a disposizione contenuti informativi, educativi e strumenti di simulazione; non consente il conferimento di incarichi di consulenza.",
      "L'eventuale avvio del servizio sarà preceduto dall'adempimento degli obblighi applicabili, inclusa l'adesione all'Arbitro per le Controversie Finanziarie (ACF), e sarà comunicato aggiornando queste informazioni.",
    ],
  },
  en: {
    title: "Service status",
    paras: [
      "The investment advice service has not yet commenced. This website provides informational and educational content and simulation tools; it does not accept investment advisory engagements.",
      "Any future launch of the service will be preceded by compliance with the applicable requirements, including adherence to the Italian Financial Disputes Arbitrator (ACF), and will be announced by updating this information.",
    ],
  },
};
export const getServiceStatusNotice = (lang) => (lang === "en" ? SERVICE_STATUS_NOTICE.en : SERVICE_STATUS_NOTICE.it);

// Iscrizione all'Albo: riferita alla persona fisica titolare del sito, NON al marchio.
// La data indicata è quella della delibera, non la data di efficacia dell'iscrizione.
export const REGISTRATION = {
  it: "Il titolare del sito è iscritto all'Albo unico dei consulenti finanziari, sezione consulenti finanziari autonomi, tenuto dall'OCF: matricola n. 640928, iscrizione disposta con delibera OCF n. 2933 del 23/03/2026.",
  en: "The website owner is registered in the Italian Single Register of Financial Advisers (Albo unico dei consulenti finanziari), independent financial advisers section, kept by the OCF: registration No. 640928, ordered by OCF resolution No. 2933 of 23/03/2026.",
};
export const getRegistration = (lang) => (lang === "en" ? REGISTRATION.en : REGISTRATION.it);

// Identità del titolare: da usare SOLO nelle sezioni identificative (Trasparenza → "Iscrizione
// all'Albo del titolare", art. 173 Reg. Intermediari; Privacy → titolare del trattamento,
// art. 13 GDPR). Non in footer, hero o altre pagine (decisione del titolare, 29/09/2026).
export const OWNER_NAME = "Paolo Balasso";
export const getRegistrationNamed = (lang) =>
  lang === "en"
    ? `The website owner, ${OWNER_NAME}, is registered in the Italian Single Register of Financial Advisers (Albo unico dei consulenti finanziari), independent financial advisers section, kept by the OCF: registration No. 640928, ordered by OCF resolution No. 2933 of 23/03/2026.`
    : `Il titolare del sito, ${OWNER_NAME}, è iscritto all'Albo unico dei consulenti finanziari, sezione consulenti finanziari autonomi, tenuto dall'OCF: matricola n. 640928, iscrizione disposta con delibera OCF n. 2933 del 23/03/2026.`;

// Helper per sapere se mostrare riferimenti OCF (matricola, delibera, ecc.)
export const showOCFData = MODE === "POST" || MODE === "PRE";
